<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { VueFinalModal, useModal } from 'vue-final-modal';
  import { ColoredButton, UiModalHeader } from '@/shared/ui';
  import ConfirmCloseModal from '@/shared/ui/modals/ConfirmCloseModal.vue';
  import { extractApiErrorMessage, useCustomToast } from '@/shared/lib';
  import { trainingCollaboratorApi } from '@/entities/Training';
  import { validateCertificateFile } from '../../lib/validateCertificateFile';
  import { readFileWithProgress } from '../../lib/readFileWithProgress';
  import type { CertificateUploadStatus } from '../../model/useCertificateUpload';
  import CertificateUploadField from './CertificateUploadField.vue';

  const props = defineProps<{
    trainingId: number;
    memberTrainingId: number;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'uploaded'): void;
  }>();

  const { showSuccessToast, showErrorToast } = useCustomToast();

  const selectedFile = ref<File | null>(null);
  const fileName = ref<string | null>(null);
  const uploadProgress = ref(0);
  const uploadStatus = ref<CertificateUploadStatus>('idle');
  const isSaving = ref(false);

  const hasFile = computed(() => selectedFile.value !== null);
  const isSaveDisabled = computed(() => !hasFile.value || uploadStatus.value !== 'completed');

  const confirmCloseModal = useModal({
    component: ConfirmCloseModal,
    attrs: {
      clickToClose: false,
      escToClose: false,
      title: 'Tem certeza que deseja fechar o Upload de Certificados?',
      description: 'Fechar cancela todas as ações realizadas na página. A ação não pode ser desfeita.',
      onClose: () => {
        confirmCloseModal.close();
      },
      onConfirm: () => {
        confirmCloseModal.close();
        resetFile();
        emit('close');
      },
    },
  });

  function resetFile(): void {
    selectedFile.value = null;
    fileName.value = null;
    uploadProgress.value = 0;
    uploadStatus.value = 'idle';
  }

  async function handleSelectFile(file: File): Promise<void> {
    const validationError = validateCertificateFile(file);

    if (validationError) {
      showErrorToast({ title: validationError, type: 'error' });
      return;
    }

    selectedFile.value = file;
    fileName.value = file.name;
    uploadProgress.value = 0;
    uploadStatus.value = 'uploading';

    try {
      await readFileWithProgress(file, (percentage) => {
        if (selectedFile.value !== file) return;
        uploadProgress.value = percentage;
      });

      // A leitura pode terminar depois de o arquivo ter sido removido ou trocado; nesse caso o
      // resultado é de outro arquivo e não pode reescrever o estado atual do campo.
      if (selectedFile.value !== file) return;

      uploadStatus.value = 'completed';
    } catch {
      if (selectedFile.value !== file) return;

      resetFile();
      showErrorToast({ title: 'Erro ao carregar o arquivo. Tente novamente', type: 'error' });
    }
  }

  function handleRemoveFile(): void {
    resetFile();
  }

  function handleClose(): void {
    if (hasFile.value) {
      confirmCloseModal.open();
      return;
    }

    emit('close');
  }

  async function handleSave(): Promise<void> {
    const file = selectedFile.value;
    if (!file || isSaving.value) return;

    isSaving.value = true;

    try {
      await trainingCollaboratorApi.uploadCollaboratorCertificate(
        props.trainingId,
        props.memberTrainingId,
        file
      );

      showSuccessToast({ title: 'Certificado enviado com sucesso', type: 'success' });
      emit('uploaded');
      resetFile();
      emit('close');
    } catch (error) {
      showErrorToast({
        title: 'Erro ao enviar certificado. Tente novamente',
        message: extractApiErrorMessage(error),
        type: 'error',
      });
    } finally {
      isSaving.value = false;
    }
  }
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-[min(90vw,560px)]"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    :click-to-close="false"
    :esc-to-close="false"
  >
    <div
      data-testid="certificate-upload-modal"
      class="flex flex-col px-8 pt-6 pb-7"
    >
      <UiModalHeader
        title="Upload de certificado"
        class="pb-6"
        @close="handleClose"
      />

      <CertificateUploadField
        :file-name="fileName"
        :upload-progress="uploadProgress"
        :upload-status="uploadStatus"
        @select-file="handleSelectFile"
        @remove-file="handleRemoveFile"
      />

      <div class="mt-8 flex justify-center gap-3">
        <ColoredButton
          data-testid="certificate-cancel"
          outlined
          color="gray-03"
          text="Cancelar"
          @click="handleClose"
        />
        <ColoredButton
          data-testid="certificate-save"
          text="Salvar"
          :disabled="isSaveDisabled"
          :loading="isSaving"
          @click="handleSave"
        />
      </div>
    </div>
  </VueFinalModal>
</template>
